import { useEffect } from "react";

export type PollContext = {
  /** Aborted once the poll stops (unmount, or `tick`, the interval or `enabled` changed): drop the answer then. */
  signal: AbortSignal;
  /** The first call since the poll (re)started. */
  first: boolean;
  /** The first call after a pause for a hidden tab: time passed without a call. */
  resumed: boolean;
};

/**
 * Calls `tick` now, then `intervalMs` after each call ENDED, so a slow answer
 * never overlaps the next call. Paused while the tab is hidden, with a call as
 * soon as it shows again. `intervalMs` null calls once and stops (a finished
 * run). A new `tick` (keep it in useCallback), interval or `enabled` starts over.
 * `immediate: false` waits one interval before the first call.
 */
export function usePolling(
  tick: (ctx: PollContext) => unknown,
  intervalMs: number | null,
  { enabled = true, immediate = true }: { enabled?: boolean; immediate?: boolean } = {},
): void {
  useEffect(() => {
    if (!enabled) return;
    const ctrl = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    let running = false;
    let first = true;
    let paused = false;
    const hidden = () => typeof document !== "undefined" && document.visibilityState === "hidden";

    const schedule = () => {
      if (intervalMs === null || ctrl.signal.aborted) return;
      if (hidden()) { paused = true; return; }
      timer = setTimeout(() => void call(), intervalMs);
    };
    const call = async () => {
      timer = undefined;
      if (running || ctrl.signal.aborted) return;
      running = true;
      const ctx = { signal: ctrl.signal, first, resumed: paused };
      first = false;
      paused = false;
      try {
        await tick(ctx);
      } catch {
        // The caller shows its own errors; a failed call is retried next time.
      } finally {
        running = false;
      }
      schedule();
    };
    const onVisibility = () => {
      if (!paused || hidden()) return;
      void call();
    };

    document.addEventListener("visibilitychange", onVisibility);
    if (immediate) void call();
    else schedule();
    return () => {
      ctrl.abort();
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [tick, intervalMs, enabled, immediate]);
}
